import { useEffect } from 'react';
import { X, ChevronLeft, Check, Loader2, Gift } from 'lucide-react';
import { LemonadeAvatar } from '@/components/auth/LemonadeAvatar';
import { useProfileWizard, PROFILE_STEPS } from '@/contexts/ProfileWizardContext';
import { fireConfetti } from '@/lib/perf/confetti';
import { Input } from '@/components/ui/input';

interface LemonadeProfileWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LemonadeProfileWizard({ isOpen, onClose }: LemonadeProfileWizardProps) {
  const {
    currentStepIndex,
    currentFieldIndex,
    currentStep,
    currentField,
    selectedValues,
    isSaving,
    completedSteps,
    progress,
    selectOption,
    setTextValue,
    continueToNext,
    goBack,
    canContinue,
    canGoBack,
    earnedEntries,
    remainingEntries,
  } = useProfileWizard();

  // Celebrate when all steps complete
  useEffect(() => {
    if (completedSteps.length === PROFILE_STEPS.length) {
      fireConfetti({ particleCount: 100, spread: 70 });
      setTimeout(onClose, 1500);
    }
  }, [completedSteps.length, onClose]);

  if (!isOpen || !currentStep || !currentField) return null;

  const currentValue = selectedValues[currentField.key];
  const isMulti = currentField.type === 'multi';

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <button
          onClick={canGoBack ? goBack : onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          {canGoBack ? (
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          ) : (
            <X className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        {/* Progress bar */}
        <div className="flex-1 mx-4 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground font-medium">
            {progress}%
          </span>
          <span className="text-xs text-primary font-medium flex items-center gap-1">
            <Gift className="w-3 h-3" />
            {earnedEntries} earned
          </span>
          {/* Always-visible close button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close wizard"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-lg mx-auto px-6 py-8 lg:py-12">
          {/* Avatar */}
          <div className="flex justify-center mb-8">
            <LemonadeAvatar />
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-1.5 mb-6">
            {PROFILE_STEPS.map((step, idx) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx < currentStepIndex
                    ? 'bg-primary'
                    : idx === currentStepIndex
                    ? 'bg-primary'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Question */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-sm font-medium text-primary">
                {currentStep.title}
              </p>
              <span className="text-xs font-bold px-2 py-0.5 bg-primary/20 text-primary rounded-full flex items-center gap-1">
                <Gift className="w-3 h-3" />
                +{currentStep.entriesReward}
              </span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-medium text-foreground mb-2">
              {currentField.question}
            </h1>
            <p className="text-muted-foreground">
              {currentStep.subtitle}
            </p>
          </div>

          {/* Options or Text Input */}
          <div className="space-y-3 mb-8">
            {currentField.type === 'text' ? (
              <Input
                type="text"
                value={typeof currentValue === 'string' ? currentValue : ''}
                onChange={(e) => setTextValue(e.target.value)}
                placeholder={currentField.placeholder || 'Enter your answer'}
                className="w-full p-4 text-lg rounded-xl border-2 border-border focus:border-primary"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canContinue) {
                    continueToNext();
                  }
                }}
              />
            ) : (
              currentField.options?.map((option) => {
                const isSelected = isMulti
                  ? Array.isArray(currentValue) && currentValue.includes(option.id)
                  : currentValue === option.id;

                return (
                  <button
                    key={option.id}
                    onClick={() => selectOption(option.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground bg-background'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded-${isMulti ? 'md' : 'full'} border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                    }`}>
                      {isSelected && (
                        isMulti 
                          ? <Check className="w-4 h-4 text-primary-foreground" />
                          : <div className="w-2 h-2 bg-primary-foreground rounded-full" />
                      )}
                    </div>
                    <span className="text-base font-medium text-foreground">
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Continue Button */}
          <button
            onClick={continueToNext}
            disabled={!canContinue || isSaving}
            className={`w-full py-4 rounded-xl font-medium text-lg transition-colors flex items-center justify-center gap-2 ${
              canContinue && !isSaving
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </button>

          {/* Skip option - available for all question types */}
          <button
            onClick={continueToNext}
            className="w-full mt-3 py-2 text-muted-foreground text-sm hover:text-foreground transition-colors"
          >
            Skip this question
          </button>

          {/* Save and finish later - always visible */}
          <button
            onClick={onClose}
            className="w-full mt-3 py-2 text-muted-foreground text-sm hover:text-foreground transition-colors underline underline-offset-2"
          >
            Save and finish later
          </button>
        </div>
      </div>
    </div>
  );
}
